/* eslint-disable class-methods-use-this */
import {
  Yuno,
  YunoInstance,
  SecureFieldInstance,
} from "@yuno-payments/sdk-web-types";
import { loadYuno } from "@shared/lib/yuno/loader";

import { DataProvider } from "@shared/constants/common";
import { recordAppError } from "@shared/utils/errors/errorLogger";
import UserBasedTicketBasketRepository from "@app-contexts/user/features/basket/data/repositories/UserBasedTicketBasketRepository/UserBasedTicketBasketRepository";

import { PAYMENT_MANAGER_CONFIRMATION_STRATEGY } from "@shared/modules/payments/utils/manager/utils";
import { extractErrorMessage } from "@shared/utils/errors/error-utils";

import { BasketRequestQueue } from "@shared/modules/basket/services/BasketRequestQueue/BasketRequestQueue";

import PaymentGateway, {
  type UpdatePaymentObjectOptions,
} from "../PaymentGateway";

import { PaymentServiceBasket } from "../../services/useBasketPaymentService";
import { PurchaseOptsType } from "../../types";
import {
  YunoCheckoutSessionResponse,
  YunoCheckoutSessionPaymentMethod,
} from "../../types/yuno";

import { PaymentConfigurationError } from "../../utils/errors/PaymentConfigurationError";
import { PaymentInitializationError } from "../../utils/errors/PaymentInitializationError";
import { PaymentProcessingError } from "../../utils/errors/PaymentProcessingError";
import { PaymentConfirmationError } from "../../utils/errors/PaymentConfirmationError";
import { isPaymentOrchestrationError } from "../../utils/errorUtils";
import { createPaymentLogger } from "../../utils/payment-logger";
import {
  buildBasePurchaseData,
  resolveAddresses,
} from "../../utils/purchase/build-purchase-data";

const log = createPaymentLogger("YunoPaymentGateway");
const basketRequestQueue = BasketRequestQueue.getInstance();

// Manually build startCheckout type as it is not exported natively by Yuno SDK types
type Status = Parameters<
  Exclude<
    Parameters<YunoInstance["startCheckout"]>[0]["yunoPaymentResult"],
    undefined
  >
>[0];

/**
 * ID for the hidden DOM container that startCheckout needs.
 * The Yuno SDK uses this element for device-fingerprinting and to
 * bootstrap 3DS redirect flows via continuePayment().
 */
const YUNO_CHECKOUT_CONTAINER_ID = "yuno-checkout-container";

/**
 * Lazily creates (or returns) a hidden DOM element that the Yuno SDK
 * can render into during startCheckout and 3DS redirect flows.
 */
function getOrCreateCheckoutContainer(): HTMLDivElement {
  let el = document.getElementById(
    YUNO_CHECKOUT_CONTAINER_ID
  ) as HTMLDivElement | null;

  if (!el) {
    el = document.createElement("div");
    el.id = YUNO_CHECKOUT_CONTAINER_ID;
    el.setAttribute("aria-hidden", "true");
    Object.assign(el.style, {
      position: "fixed",
      left: "-9999px",
      top: "-9999px",
      width: "0",
      height: "0",
      overflow: "hidden",
      pointerEvents: "none",
    });
    document.body.appendChild(el);
  }

  return el;
}

/**
 * Payment Gateway implementation for Yuno
 *
 * Uses SecureFields API for mounting manual card entry components and handling saved cards (vaulted tokens)
 * Uses External Buttons API for mounting wallet elements
 *
 */
class YunoPaymentGateway extends PaymentGateway {
  public static gatewayName = "YunoPaymentGateway";

  /** The Yuno SDK global object */
  sdk: Yuno | null = null;

  /** The initialized Yuno instance */
  instance: YunoInstance | null = null;

  /** SecureFields instance for managing checkout UI */
  elementsInstance: SecureFieldInstance | undefined = undefined;

  /** Promise for SDK initialization */
  initializationPromise: Promise<Yuno | null> | undefined = undefined;

  /** Public API key for Yuno */
  publicApiKey: string | undefined = undefined;

  /** Current checkout session ID */
  checkoutSession: string | undefined = undefined;

  /** Full checkout session data from the backend */
  checkoutSessionData: YunoCheckoutSessionResponse | undefined = undefined;

  /** Selected vaulted token for saved card payments */
  private selectedVaultedToken: string | undefined = undefined;

  /** List of listeners to notify that we have initialized. Fires on both first and subsequent runs. */
  private initializationListener: (() => void) | null = null;

  private willReinitializeListener: (() => void) | null = null;

  /**
   * Deferred promise that blocks `purchase()` until `yunoPaymentResult`
   * fires with a terminal status. Both card and wallet flows create this
   * before calling `continuePayment()`, so the caller (PaymentManager for
   * both cards and wallets) only resumes on a confirmed outcome.
   */
  private paymentResultDeferred: {
    promise: Promise<void>;
    resolve: () => void;
    reject: (error: Error) => void;
  } | null = null;

  private createPaymentResultDeferred(): Promise<void> {
    let resolve!: () => void;
    let reject!: (error: Error) => void;
    const promise = new Promise<void>((res, rej) => {
      resolve = res;
      reject = rej;
    });
    this.paymentResultDeferred = { promise, resolve, reject };
    return promise;
  }

  /**
   * Initializes this YunoPaymentGateway by loading the SDK and
   * initializing it with the public API key
   */
  async initialize(basket: PaymentServiceBasket) {
    log.info("Initializing");

    if (!this.initializationPromise) {
      this.initializationPromise = loadYuno();
    }

    if (!basket?.paymentGateway) {
      throw new PaymentConfigurationError(
        "Tried to initialize a YunoPaymentGateway with a basket that has no paymentGateway",
        {
          metadata: {
            gatewayName: YunoPaymentGateway.gatewayName,
            operation: "initialize",
            missingField: "paymentGateway",
          },
        }
      );
    }

    if (!basket.paymentGateway.publishable_api_key) {
      throw new PaymentConfigurationError(
        "Tried to initialize a YunoPaymentGateway with a misconfigured paymentGateway",
        {
          metadata: {
            gatewayName: YunoPaymentGateway.gatewayName,
            operation: "initialize",
            missingCredentials: {
              publishableApiKey: !basket.paymentGateway.publishable_api_key,
            },
          },
        }
      );
    }

    this.publicApiKey = basket.paymentGateway.publishable_api_key;

    try {
      this.sdk = await this.initializationPromise;

      if (!this.sdk) {
        throw new PaymentInitializationError("Yuno SDK failed to load", {
          metadata: {
            gatewayName: YunoPaymentGateway.gatewayName,
            operation: "initialize",
          },
        });
      }

      // Initialize Yuno instance with public API key
      this.instance = await this.sdk.initialize(this.publicApiKey);
      window.yuno = this.instance; // For debugging purposes

      // Create checkout session with the backend
      const checkoutSessionResponse = await this.createCheckoutSession(
        DataProvider.User,
        basket
      );

      // Ensure the hidden container exists before handing its selector to
      // startCheckout. The SDK uses this element for device-fingerprinting
      // and to bootstrap 3DS redirect flows.
      getOrCreateCheckoutContainer();

      await this.instance.startCheckout({
        checkoutSession: checkoutSessionResponse.checkout_session,
        elementSelector: `#${YUNO_CHECKOUT_CONTAINER_ID}`,
        countryCode: checkoutSessionResponse.country,
        language: "en",
        showPaymentStatus: false,
        showLoading: false,
        // Entry point for wallet-driven payments (e.g. Google Pay, Apple Pay).
        // The Yuno SDK invokes this callback when the user authorizes a wallet
        // payment. We pass the OTT to the PaymentManager via
        // onWalletPaymentAuthorized, which routes through the standard
        // manager.purchase() flow (prePurchase → processPurchase → postPurchase).
        // Error handling is owned by PaymentManager.purchase().
        yunoCreatePayment: async (oneTimeToken, _tokenWithInformation) => {
          try {
            log.info("Received one-time token", {
              hasToken: Boolean(oneTimeToken),
            });
            return await this.onWalletPaymentAuthorized?.(oneTimeToken);
          } catch (error) {
            log.error("yunoCreatePayment wallet callback failed", {
              errorMessage: extractErrorMessage(error),
              hasToken: Boolean(oneTimeToken),
            });
            recordAppError(
              error instanceof Error
                ? error
                : new Error(
                    `yunoCreatePayment failed: ${extractErrorMessage(error)}`
                  )
            );
            return null;
          }
        },
        yunoPaymentResult: (status) => {
          return this.handlePaymentResult(status);
        },
        yunoError: (message, data) => {
          // Silently record app error. No business logic as business logic is expected to be handled by the callee / control flow
          // where the error originated.
          log.error("yunoError callback fired", {
            message: String(message),
            data: data as Record<string, unknown>,
          });
          recordAppError(message);
        },
        onLoading: ({ isLoading }) => {
          this.onProcessing?.(isLoading);
        },
      });

      // Create elements instance based on SecureFields API.
      // Only used for manual card entry & saved cards.
      // For wallets, see YunoWalletElement.
      this.elementsInstance = await this.instance.secureFields({
        countryCode: checkoutSessionResponse.country,
        checkoutSession: checkoutSessionResponse.checkout_session,
        installmentEnable: false,
      });

      log.info("SDK initialized successfully", {
        hasInstance: Boolean(this.instance),
        hasElementsInstance: Boolean(this.elementsInstance),
        hasCheckoutSession: Boolean(this.checkoutSession),
      });

      this.emitInitializationComplete();
    } catch (error) {
      // Clear the failed promise to allow retries on subsequent calls
      this.initializationPromise = undefined;
      this.sdk = null;
      this.instance = null;
      this.elementsInstance = undefined;

      log.error("Initialization failed, cleared for retry", {
        errorMessage: extractErrorMessage(error),
      });

      if (isPaymentOrchestrationError(error)) {
        throw error;
      }

      throw new PaymentInitializationError(
        `Yuno SDK initialization failed: ${extractErrorMessage(error)}`,
        {
          metadata: {
            gatewayName: YunoPaymentGateway.gatewayName,
            operation: "initialize",
            originalError: extractErrorMessage(error),
          },
        }
      );
    }

    return this;
  }

  /**
   * Reinitialize the Yuno SDK with a fresh checkout session.
   * Called by PaymentManagerContext when the basket price changes.
   * Throws on missing basket or instance — a price-change reinit that
   * can't run is a bug in the calling code.
   *
   * Use `renewCheckoutSession()` instead for error-recovery paths.
   */
  public async reinitialize(
    basket?: Pick<
      PaymentServiceBasket,
      "basketId" | "basketUuid" | "basketUuid2" | "currency" | "totalPrice"
    >
  ): Promise<this> {
    log.info("Reinitializing for price change");

    if (!basket) {
      throw new PaymentConfigurationError(
        "Tried to initialize a YunoPaymentGateway with a basket that has no basket",
        {
          metadata: {
            gatewayName: YunoPaymentGateway.gatewayName,
            operation: "initialize",
            missingField: "paymentGateway",
          },
        }
      );
    }

    if (!this.instance) {
      if (this.initializationPromise) {
        log.info(
          "Skipping reinitialize — initial initialization still in progress"
        );
        return this;
      }

      throw new PaymentInitializationError(
        "Tried to re-initialize a YunoPaymentGateway without an existing Yuno instance in the first place",
        {
          metadata: { gatewayName: "YunoPaymentGateway" },
        }
      );
    }

    this.emitWillReinitialize();

    const checkoutSessionResponse = await this.createCheckoutSession(
      DataProvider.User,
      basket
    );

    await this.instance.updateCheckoutSession(
      checkoutSessionResponse.checkout_session
    );

    this.emitInitializationComplete();

    return this;
  }

  /**
   * Creates a fresh checkout session after a payment failure so the user
   * can retry without a stale session. Reads the basket from
   * basketPaymentService and silently no-ops on missing state — session
   * renewal failure should never mask the original payment error.
   *
   * Use `reinitialize(basket)` instead for normal basket-change flows.
   */
  public async renewCheckoutSession(): Promise<void> {
    const basket = this.basketPaymentService?.basket as
      | PaymentServiceBasket
      | undefined;

    if (!basket || !this.instance) {
      log.warn("Cannot renew checkout session — missing basket or instance", {
        hasBasket: Boolean(basket),
        hasInstance: Boolean(this.instance),
      });
      return;
    }

    this.emitWillReinitialize();

    const checkoutSessionResponse = await this.createCheckoutSession(
      DataProvider.User,
      basket
    );

    await this.instance.updateCheckoutSession(
      checkoutSessionResponse.checkout_session
    );

    this.emitInitializationComplete();
  }

  /**
   * Creates a Yuno checkout session via the backend API
   */
  private async createCheckoutSession(
    _basketType: DataProvider,
    basket: PaymentServiceBasket
  ): Promise<YunoCheckoutSessionResponse> {
    log.info("Creating checkout session", {
      basketId: basket.basketId,
    });

    if (!basket.basketId || !basket.basketUuid) {
      throw new PaymentConfigurationError(
        "Cannot create checkout session - basket ID or UUID is missing",
        {
          metadata: {
            gatewayName: YunoPaymentGateway.gatewayName,
            operation: "createCheckoutSession",
            hasBasketId: Boolean(basket.basketId),
            hasBasketUuid: Boolean(basket.basketUuid),
          },
        }
      );
    }

    const { basketId, basketUuid } = basket;

    // Always use UserBasedTicketBasketRepository for Yuno checkout sessions
    const repository = new UserBasedTicketBasketRepository();

    const response = await basketRequestQueue.addBasketRequest(async () => {
      return repository.createYunoCheckoutSession({
        id: basketId,
        uuid: basketUuid,
      });
    });

    this.checkoutSessionData = response;
    this.checkoutSession = response.checkout_session;

    log.info("Checkout session created", {
      checkoutSession: response.checkout_session,
      country: response.country,
      paymentMethodsCount: response.payment_methods.length,
    });

    return response;
  }

  /**
   * SDK callback handler. Acts purely as a deferred resolver/rejecter —
   * the calling code (PaymentManager for both cards and wallets) owns the
   * success/failure path after purchase() returns or throws.
   */
  private handlePaymentResult(status: Status): void {
    /* eslint-disable no-fallthrough */
    switch (status) {
      case "FAIL":
      case "REJECT":
      // @ts-expect-error -- Yuno SDK types inaccurate
      case "REJECTED":
      // @ts-expect-error -- Yuno SDK types inaccurate
      case "CANCELED":
      // @ts-expect-error -- Yuno SDK types inaccurate
      case "DECLINED": {
        this.paymentResultDeferred?.reject(
          new PaymentConfirmationError(
            "Your payment could not be processed. Please try again.",
            {
              userMessage:
                "Your payment could not be processed. Please try again.",
              metadata: { yunoStatus: status },
            }
          )
        );
        this.paymentResultDeferred = null;
        break;
      }
      // @ts-expect-error -- Yuno SDK types inaccurate
      case "PENDING":
      case "PROCESSING": {
        this.onProcessing?.(true);
        break;
      }
      case "SUCCEEDED": {
        this.paymentResultDeferred?.resolve();
        this.paymentResultDeferred = null;
        break;
      }
      // @ts-expect-error -- Yuno SDK types inaccurate
      case "ERROR":
      default: {
        this.paymentResultDeferred?.reject(
          new PaymentConfirmationError(
            "Something went wrong with your payment. Please try again.",
            {
              userMessage:
                "Something went wrong with your payment. Please try again.",
              metadata: { yunoStatus: status },
            }
          )
        );
        this.paymentResultDeferred = null;
      }
    }
    /* eslint-enable no-fallthrough */
  }

  public async updatePaymentObject(
    basket?: Pick<
      PaymentServiceBasket,
      "basketId" | "basketUuid" | "basketUuid2" | "currency" | "totalPrice"
    >,
    options?: UpdatePaymentObjectOptions
  ) {
    if (!basket) {
      log.info("updatePaymentObject skipped — no basket provided");
      return this;
    }

    // Yuno OTTs are bound to the checkout session they were generated under.
    // Reinitializing here would create a new session and invalidate the token.
    if (
      options?.confirmationStrategy ===
      PAYMENT_MANAGER_CONFIRMATION_STRATEGY.WALLET
    ) {
      return this;
    }

    // Full re-initialization to ensure wallet buttons get updated amounts
    await this.reinitialize(basket);

    return this;
  }

  /**
   * Handles errors from Axios-based /purchase calls and other non-SDK
   * error paths. Pre-classified PaymentOrchestrationErrors pass through
   * unchanged. Unknown errors default to PaymentProcessingError (Sentry).
   */
  private handleConfirmErrorWithCleanup(error: unknown): never {
    this.paymentResultDeferred = null;
    return this.handleConfirmError(error);
  }

  /**
   * Begin a confirmation flow using Yuno's checkout
   */
  public async confirmPayment(
    confirmationStrategy: PAYMENT_MANAGER_CONFIRMATION_STRATEGY,
    basketType: DataProvider,
    basket: PaymentServiceBasket,
    purchaseOpts: PurchaseOptsType = {}
  ) {
    log.info(`Confirming payment: ${confirmationStrategy}`, {
      hasSavedCard: Boolean(this.selectedVaultedToken),
    });

    switch (confirmationStrategy) {
      case PAYMENT_MANAGER_CONFIRMATION_STRATEGY.WALLET: {
        return this.confirmWalletPayment(basketType, basket, purchaseOpts);
      }
      case PAYMENT_MANAGER_CONFIRMATION_STRATEGY.MANUAL_CARD_ENTRY:
      default: {
        return this.confirmCardPayment(basketType, basket, purchaseOpts);
      }
    }
  }

  /**
   * Confirm a wallet payment using a one-time token from the SDK callback.
   * The OTT arrives via `purchaseOpts.wallet_one_time_token`, set by
   * YunoWalletElement's onWalletPaymentAuthorized handler.
   */
  private async confirmWalletPayment(
    basketType: DataProvider,
    basket: PaymentServiceBasket,
    purchaseOpts: PurchaseOptsType = {}
  ) {
    const oneTimeToken = purchaseOpts.wallet_one_time_token;

    if (!oneTimeToken) {
      throw new PaymentProcessingError(
        "Wallet confirmation called without a one-time token",
        {
          metadata: {
            gatewayName: YunoPaymentGateway.gatewayName,
            operation: "confirmWalletPayment",
          },
          userMessage: "Cannot process wallet payment. Please try again.",
        }
      );
    }

    return this.purchase(basketType, basket, oneTimeToken, purchaseOpts);
  }

  /**
   * Confirm a card payment using an OTT (one-time token)
   */
  private async confirmCardPayment(
    basketType: DataProvider,
    basket: PaymentServiceBasket,
    purchaseOpts: PurchaseOptsType = {}
  ) {
    if (!this.instance) {
      throw new PaymentInitializationError(
        "Tried to confirm a card payment without a Yuno instance",
        {
          metadata: { gatewayName: "YunoPaymentGateway" },
        }
      );
    }

    if (!this.elementsInstance) {
      throw new PaymentInitializationError(
        "Tried to confirm a card payment without a Yuno SecureFields instance",
        {
          metadata: { gatewayName: "YunoPaymentGateway" },
        }
      );
    }

    try {
      const hasVaultedToken =
        basket?.yunoPaymentMethod !== null &&
        basket?.yunoPaymentMethod !== undefined;

      const oneTimeTokenWithInformation = hasVaultedToken
        ? await this.elementsInstance.generateTokenWithInformation({
            checkoutSession: this.checkoutSession,
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore -- SDK types are inaccurate as of 5.0.
            vaultedToken: basket?.yunoPaymentMethod || undefined,
          })
        : await this.elementsInstance.generateTokenWithInformation({
            checkoutSession: this.checkoutSession,
            cardHolderName: purchaseOpts.name_on_card || "",
            saveCard: Boolean(basket?.saveUserPaymentMethod),
          });

      return this.purchase(
        basketType,
        basket,
        oneTimeTokenWithInformation.token,
        purchaseOpts
      );
    } catch (error) {
      return this.handleConfirmErrorWithCleanup(error);
    }
  }

  /**
   * Performs a `/purchase` call to the basket with the Yuno one-time token.
   * Uses basketPaymentService.purchase() for repository selection.
   */
  private async purchase(
    basketType: DataProvider,
    basket: PaymentServiceBasket,
    oneTimeToken?: string,
    purchaseOpts: PurchaseOptsType = {}
  ) {
    log.info("Purchasing with OTT");

    if (!this.instance) {
      throw new PaymentInitializationError(
        "Tried to confirm without a Yuno instance",
        {
          metadata: { gatewayName: "YunoPaymentGateway" },
        }
      );
    }

    if (!basket.basketId) {
      throw new PaymentProcessingError(
        "Tried to confirm payment but basket ID is missing",
        {
          metadata: {
            gatewayName: YunoPaymentGateway.gatewayName,
            operation: "purchase",
          },
          userMessage:
            "Cannot process payment because the basket ID is missing. Please refresh the page and try again.",
        }
      );
    }

    if (!this.basketPaymentService) {
      throw new PaymentProcessingError(
        "Tried to confirm payment but basketPaymentService is not available",
        {
          metadata: {
            gatewayName: YunoPaymentGateway.gatewayName,
            operation: "purchase",
          },
        }
      );
    }

    const purchaseData = buildBasePurchaseData(
      basketType,
      basket,
      purchaseOpts
    );

    if (oneTimeToken) {
      purchaseData.credit_card = {
        charge_type: "ct_token",
        token_id: oneTimeToken,
      };
    }

    const { billingAddress, shippingAddress } = resolveAddresses(
      basket,
      purchaseOpts
    );

    if (billingAddress) {
      purchaseData.billing_address = billingAddress;
    }

    if (shippingAddress) {
      purchaseData.shipping_address = shippingAddress;
    }

    return this.basketPaymentService
      .purchase(basket.basketId, purchaseData)
      .then(async (result) => {
        try {
          if (!this.instance) {
            throw new PaymentInitializationError(
              "Tried to confirm without a Yuno instance",
              {
                metadata: { gatewayName: "YunoPaymentGateway" },
              }
            );
          }

          const resultPromise = this.createPaymentResultDeferred();

          await this.instance.continuePayment();

          let timeoutId: ReturnType<typeof setTimeout>;

          await Promise.race([
            resultPromise.finally(() => clearTimeout(timeoutId)),
            new Promise<never>((_resolve, reject) => {
              timeoutId = setTimeout(
                () =>
                  reject(
                    new PaymentProcessingError(
                      "Timed out waiting for payment result from Yuno SDK",
                      {
                        metadata: {
                          gatewayName: "YunoPaymentGateway",
                          operation: "purchase",
                        },
                        userMessage:
                          "Payment confirmation timed out. Please check your order status or try again.",
                      }
                    )
                  ),
                90_000
              );
            }),
          ]);
        } catch (err) {
          return this.handleConfirmErrorWithCleanup(err);
        }

        return result;
      })
      .catch((err) => {
        return this.handleConfirmErrorWithCleanup(err);
      });
  }

  async postProcessCheckout() {
    log.info("Checking for invoice");

    if (!this.basketPaymentService || !this.invoicePaymentService) {
      throw new PaymentProcessingError(
        "Tried to complete checkout but required payment services are not available",
        {
          metadata: {
            gatewayName: YunoPaymentGateway.gatewayName,
            operation: "postProcessCheckout",
            hasBasketPaymentService: Boolean(this.basketPaymentService),
            hasInvoicePaymentService: Boolean(this.invoicePaymentService),
          },
          userMessage:
            "Cannot complete payment processing because required payment services are not available.",
        }
      );
    }

    // Poll for the basket until we get a valid invoice_id
    const MAX_BASKET_ATTEMPTS = 10;
    let finalBasket;
    let invoiceId = null;

    for (let attempts = 1; attempts <= MAX_BASKET_ATTEMPTS; attempts++) {
      // eslint-disable-next-line no-await-in-loop
      finalBasket = await this.basketPaymentService.refresh({
        no_lock: true,
      });

      if (finalBasket?.id == null) {
        throw new PaymentProcessingError(
          "Tried to post-process checkout but cannot retrieve basket information",
          {
            metadata: {
              gatewayName: YunoPaymentGateway.gatewayName,
              operation: "postProcessCheckout",
              attempts,
              maxAttempts: MAX_BASKET_ATTEMPTS,
            },
            userMessage:
              "Unable to retrieve basket information after payment processing. Please check your order status or contact support.",
          }
        );
      }

      // If we have an invoice ID, break out of the polling loop
      if (finalBasket.invoice_id != null) {
        invoiceId = finalBasket.invoice_id;
        break;
      }

      // Wait before trying again if we haven't found an invoice_id yet
      if (attempts < MAX_BASKET_ATTEMPTS) {
        // eslint-disable-next-line no-await-in-loop
        await new Promise((resolve) => setTimeout(resolve, 2000));
      }
    }

    // If we couldn't get an invoice_id after all attempts, throw an error
    if (invoiceId == null) {
      throw new PaymentProcessingError(
        "Tried to post-process checkout but invoice generation failed",
        {
          metadata: {
            gatewayName: YunoPaymentGateway.gatewayName,
            operation: "postProcessCheckout",
            maxAttempts: MAX_BASKET_ATTEMPTS,
            finalBasketId: finalBasket?.id,
          },
          userMessage:
            "Payment was processed but invoice could not be generated after maximum attempts. Please contact support to confirm your order status.",
        }
      );
    }

    if (finalBasket == null) {
      throw new PaymentProcessingError(
        "Tried to post-process checkout but cannot retrieve final basket",
        {
          metadata: {
            gatewayName: YunoPaymentGateway.gatewayName,
            operation: "postProcessCheckout",
            invoiceId,
          },
          userMessage:
            "Unable to retrieve final basket information after payment processing. Please contact support to confirm your order.",
        }
      );
    }

    return finalBasket;
  }

  // ============================================
  // Saved Payment Methods (Vaulted Cards)
  // ============================================

  /**
   * Get saved (vaulted) payment methods from checkout session
   * These are cards that the customer has previously saved
   */
  public getSavedPaymentMethods(): YunoCheckoutSessionPaymentMethod[] {
    return (
      this.checkoutSessionData?.payment_methods?.filter(
        (pm) => pm.vaulted_token !== null
      ) ?? []
    );
  }

  /**
   * Check if there are saved payment methods available
   */
  public hasSavedPaymentMethods(): boolean {
    return this.getSavedPaymentMethods().length > 0;
  }

  // ============================================
  // Initialization Meta-functions
  // ============================================

  public onInitialization(cb: () => void): () => void {
    this.initializationListener = cb;
    return () => {
      if (this.initializationListener === cb) {
        this.initializationListener = null;
      }
    };
  }

  public onWillReinitialize(cb: () => void): () => void {
    this.willReinitializeListener = cb;
    return () => {
      if (this.willReinitializeListener === cb) {
        this.willReinitializeListener = null;
      }
    };
  }

  private emitWillReinitialize(): void {
    this.willReinitializeListener?.();
  }

  private emitInitializationComplete(): void {
    this.initializationListener?.();
  }
}

export default YunoPaymentGateway;
